import { canOperateDevice } from "@/lib/permissions";
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { STATUS, statusLabel, fmtBytes, fmtDate, metricLabel, stateText } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, MapPin, Hash, Wifi, WifiOff, Cpu, Activity, CalendarRange, RefreshCw, Download, Network, ExternalLink, Copy, KeyRound, ShieldCheck, Play } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { useLang } from "@/context/LanguageContext";
import { useRefresh } from "@/context/RefreshContext";
import { toast } from "sonner";
import { DeviceReports } from "@/components/DeviceReports";
import { ProtocolStatus } from "@/components/ProtocolStatus";
import { DeviceControl } from "@/components/DeviceControl";
import { FilterHistory } from "@/components/FilterHistory";
import { useAuth } from "@/context/AuthContext";
import { completeMetricDefinitions, normalizeMetricSeries, normalizeMetricValues } from "@/lib/metrics.mjs";
import { InfoHint, ValueGuideButton } from "@/components/InfoHint";
import { canManageFiltering, canMeasure, isAdmin, isDemo } from "@/lib/permissions";
import { NextMeasurement } from "@/components/NextMeasurement";

const PERIODS = [
  { k: "1h", nl: "Afgelopen uur", en: "Past hour" },
  { k: "24h", nl: "24 uur", en: "24 hours" }, { k: "7d", nl: "7 dagen", en: "7 days" }, { k: "30d", nl: "30 dagen", en: "30 days" },
  { k: "3m", nl: "3 maanden", en: "3 months" }, { k: "1y", nl: "1 jaar", en: "1 year" },
];
const LINE_COLORS = ["#f97316", "#eab308", "#3b82f6", "#10b981", "#a855f7", "#ec4899", "#14b8a6", "#ef4444", "#84cc16"];
const num = (v, lang = "nl") => (v == null ? "—" : Number(v).toLocaleString(lang === "en" ? "en-GB" : "nl-NL", { maximumFractionDigits: 2 }));

// Render label but keep "µm" in normal case (uppercase distorts µ → looks like M)
function Lbl({ text }) {
  const parts = String(text).split(/(µm)/g);
  return <>{parts.map((p, i) => (p === "µm" ? <span key={i} className="normal-case">µm</span> : p))}</>;
}
function fmtDuration(s, lang = "nl") {
  if (s == null) return "—";
  s = Math.round(Number(s));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} m ${s % 60} s`;
  if (s < 86400) return `${Math.floor(s / 3600)} ${lang === "en" ? "h" : "u"} ${Math.floor((s % 3600) / 60)} m`;
  return `${Math.floor(s / 86400)} d ${Math.floor((s % 86400) / 3600)} ${lang === "en" ? "h" : "u"}`;
}

function zone(value, warn, crit, profileLimit) {
  if (value == null) return "grey";
  if (profileLimit != null) return Number(value) > Number(profileLimit) ? "orange" : "green";
  if (crit != null && value >= crit) return "red";
  if (warn != null && value >= warn) return "orange";
  if (warn != null || crit != null) return "green";
  return "none";
}
const DOT = { green: "bg-emerald-500", orange: "bg-amber-500", blue: "bg-blue-500", red: "bg-red-500", grey: "bg-slate-400", none: "bg-transparent" };
const VAL = { green: "text-foreground", orange: "text-amber-500", blue: "text-blue-500", red: "text-red-500", grey: "text-muted-foreground", none: "text-foreground" };

function MetricCell({ def, value }) {
  const { lang } = useLang();
  const z = zone(value, def.warning, def.critical, def.profile_limit);
  return (
    <div className="bg-card px-2.5 py-1.5" data-testid={`metric-${def.key}`}>
      <div className="flex items-center gap-1.5">
        {z !== "none" && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT[z]}`} />}
        <span className="truncate text-[9px] font-bold uppercase tracking-[0.1em] text-muted-foreground"><Lbl text={metricLabel(def.key, def.label, lang)} /></span><InfoHint helpKey={def.key} />
      </div>
      <div className={`font-mono text-base font-medium leading-tight tracking-tight ${VAL[z]}`}>
        {def.key === "uptime_seconds"
          ? fmtDuration(value, lang)
          : <>{num(value, lang)}{def.unit && value != null ? <span className="ml-1 text-[10px] text-muted-foreground">{def.unit}</span> : null}</>}
      </div>
    </div>
  );
}

function systemStatus(current, t) {
  if (current.status === 4) return { text: t("Storing", "Fault"), color: "red" };
  if (current.status === 3) return { text: t("Filteren actief", "Filtering"), color: "blue" };
  if (current.status === 2) return { text: t("Meten", "Measuring"), color: "green" };
  if (current.status === 1 || current.warmup === 1) return { text: t("Opwarmen", "Warming up"), color: "orange" };
  if (current.status === 0) return { text: t("Wachten", "Waiting"), color: "grey" };
  return { text: "—", color: "grey" };
}

function DerivedStatusCell({ current, t }) {
  const st = systemStatus(current, t);
  const c = { green: "text-emerald-500", red: "text-red-500", orange: "text-amber-500", blue: "text-blue-500", grey: "text-muted-foreground" }[st.color] || "text-muted-foreground";
  return (
    <div className="bg-card px-2.5 py-1.5" data-testid="state-tile-system-status">
      <div className="flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 rounded-full ${DOT[st.color] || DOT.grey}`} />
        <span className="truncate text-[9px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{t("Status", "Status")}</span><InfoHint helpKey="process_status" />
      </div>
      <div className={`font-heading text-sm font-bold leading-tight ${c}`}>{st.text}</div>
    </div>
  );
}

function StateCell({ def, value }) {
  const { lang } = useLang();
  const map = def.states || {};
  const st = map[String(value)] || (value != null ? { text: String(value), color: "grey" } : { text: "—", color: "grey" });
  const c = { green: "text-emerald-500", red: "text-red-500", orange: "text-amber-500", blue: "text-blue-500", grey: "text-muted-foreground" }[st.color] || "text-muted-foreground";
  return (
    <div className="bg-card px-2.5 py-1.5" data-testid={`state-tile-${def.key}`}>
      <div className="flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 rounded-full ${DOT[st.color] || DOT.grey}`} />
        <span className="truncate text-[9px] font-bold uppercase tracking-[0.1em] text-muted-foreground"><Lbl text={metricLabel(def.key, def.label, lang)} /></span>
      </div>
      <div className={`font-heading text-sm font-bold leading-tight ${c}`}>{stateText(st.text, lang)}</div>
    </div>
  );
}

function CustomRange({ active, range, onApply }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(range.start ? range.start.slice(0, 16) : "");
  const [end, setEnd] = useState(range.end ? range.end.slice(0, 16) : "");
  const apply = () => {
    if (!start || !end) return;
    onApply({ start: new Date(start).toISOString(), end: new Date(end).toISOString() });
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="period-custom"
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
            active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
          <CalendarRange className="h-3.5 w-3.5" /> {active && range.start ? `${new Date(range.start).toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL")} – ${new Date(range.end).toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL")}` : t("Aangepast", "Custom")}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-3">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("Eigen periode", "Custom period")}</div>
          <div className="space-y-1.5"><Label className="text-xs">{t("Van", "From")}</Label><Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="custom-start" /></div>
          <div className="space-y-1.5"><Label className="text-xs">{t("Tot", "To")}</Label><Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="custom-end" /></div>
          <Button size="sm" className="w-full" onClick={apply} data-testid="custom-apply">{t("Toepassen", "Apply")}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RemoteAccessPanel({ deviceId }) {
  const { t } = useLang();
  const [remote, setRemote] = useState(null);

  useEffect(() => {
    let active = true;
    api.get(`/devices/${deviceId}/remote-access`)
      .then((response) => { if (active) setRemote(response.data.remote_access); })
      .catch(() => { if (active) setRemote(false); });
    return () => { active = false; };
  }, [deviceId]);

  if (!remote || !remote.enabled) return null;

  const copy = async (value, label) => {
    await navigator.clipboard.writeText(value || "");
    toast.success(t(`${label} gekopieerd`, `${label} copied`));
  };
  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid="remote-access-panel">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Network className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-sm font-bold">RevPi via Tailscale</h2>
            <Badge variant="outline" className="gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{t("Tailscale ingesteld", "Tailscale configured")}</Badge>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-muted-foreground">
            <span>{remote.vpn_device_name || t("Naam onbekend", "Name unknown")}</span>
            <span>{remote.vpn_address}</span>
          </div>
        </div>
        <Button className="gap-2" onClick={() => window.open(remote.connect_url, "_blank", "noopener,noreferrer")} data-testid="open-revpi-btn">
          <ExternalLink className="h-4 w-4" /> {t("Open RevPi", "Open RevPi")}
        </Button>
      </div>

      <div className="mt-4 border-t border-border pt-4">
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5 text-xs"><KeyRound className="h-3.5 w-3.5" /> {t("Gebruikersnaam", "Username")}</Label>
          <div className="flex max-w-md gap-2"><Input readOnly value={remote.revpi_username ?? ""} className="font-mono text-xs" /><Button variant="outline" size="icon" onClick={() => copy(remote.revpi_username, t("Gebruikersnaam", "Username"))} disabled={!remote.revpi_username}><Copy className="h-4 w-4" /></Button></div>
          <p className="text-[11px] text-muted-foreground">{t("Het RevPi-wachtwoord is afgeschermd en kan door beheerders in de installatiewizard of VPN-instellingen worden getoond.", "The RevPi password is protected and can be viewed by administrators in the installation wizard or VPN settings.")}</p>
        </div>
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">{t("Open RevPi gebruikt het opgeslagen Tailscale-adres. Tailscale moet op deze computer verbonden zijn.", "Open RevPi uses the stored Tailscale address. Tailscale must be connected on this computer.")}</p>
    </section>
  );
}

function CleanlinessProfile({ device, current }) {
  const { t, lang } = useLang();
  const profile = device?.profile;
  if (!profile) return null;
  const start = `${profile.start.iso4}/${profile.start.iso6}/${profile.start.iso14}`;
  const stop = `${profile.stop.iso4}/${profile.stop.iso6}/${profile.stop.iso14}`;
  const actualValues = [current?.ISO_4um, current?.ISO_6um, current?.ISO_14um];
  const actual = actualValues.every((value) => value == null) ? "—/—/—" : actualValues.map((value) => value == null ? "—" : Number(value).toLocaleString(lang === "en" ? "en-GB" : "nl-NL", { maximumFractionDigits: 1 })).join("/");
  const actualColor = device.status_color === "orange" ? "text-amber-600" : device.status_color === "green" ? "text-emerald-600" : "text-muted-foreground";
  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid="cleanliness-profile">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2 text-primary"><ShieldCheck className="h-4 w-4" /></div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{t("Actief reinheidsprofiel", "Active cleanliness profile")}</div>
            <h2 className="mt-1 text-sm font-bold">{({ standard_hydraulics: t("Conventionele industriële hydrauliek", "Conventional industrial hydraulics"), piston_pump_system: t("Plunjerpomp- en hydrostatische systemen", "Piston pump and hydrostatic systems"), proportional_hydraulics: t("Proportioneel geregelde hydrauliek", "Proportionally controlled hydraulics") })[profile.code] || profile.label}</h2>
            <p className="mt-1 text-[11px] text-muted-foreground">{t("Dit profiel koppelt de gevoeligheid van het hydraulische systeem aan de juiste ISO-grens.", "This profile links the hydraulic system's sensitivity to the correct ISO limit.")}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:min-w-[590px]">
          <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t("Inschakelgrens filtering", "Filtering start limit")}<InfoHint helpKey="profile_limit" /></div>
            <div className="mt-1 font-mono text-base font-semibold text-amber-600">ISO {start}</div>
            <div className="mt-1 text-[9px] leading-3 text-muted-foreground">{t("Start bij overschrijding van minimaal één ISO-waarde.", "Starts when at least one ISO value is exceeded.")}</div>
          </div>
          <div className="rounded-md border border-border bg-muted/20 px-3 py-2" data-testid="current-cleanliness-profile-value">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t("Actuele oliereinheid", "Current oil cleanliness")}</div>
            <div className={`mt-1 font-mono text-base font-semibold ${actualColor}`}>ISO {actual}</div>
            <div className="mt-1 text-[9px] leading-3 text-muted-foreground">{t("Laatste gemeten ISO-set zoals door de RevPi aangeleverd.", "Latest ISO set supplied by the RevPi.")}</div>
          </div>
          <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t("Reinigingsdoel", "Cleaning target")}</div>
            <div className="mt-1 font-mono text-base font-semibold text-emerald-600">ISO {stop}</div>
            <div className="mt-1 text-[9px] leading-3 text-muted-foreground">{t("Stop zodra alle drie de ISO-waarden dit niveau halen.", "Stops when all three ISO values reach this level.")}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TimelineChart({ title, defs, series, period, storageKey }) {
  const { lang } = useLang();
  const [visible, setVisible] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(storageKey) || "null"); if (s) return s; } catch {}
    return Object.fromEntries(defs.map((m) => [m.key, true]));
  });
  useEffect(() => { localStorage.setItem(storageKey, JSON.stringify(visible)); }, [visible, storageKey]);
  if (!defs.length) return null;
  const toggle = (k) => setVisible((v) => ({ ...v, [k]: v[k] === false ? true : false }));
  const shown = defs.filter((m) => visible[m.key] !== false);
  return (
    <div className="min-w-0 rounded-lg border border-border bg-card p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-heading text-sm font-bold tracking-tight">{title}</h3>
      </div>
      <div className="mb-3 flex flex-wrap gap-1.5" data-testid={`series-picker-${storageKey}`}>
        {defs.map((m, i) => {
          const on = visible[m.key] !== false;
          return (
            <button key={m.key} onClick={() => toggle(m.key)}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all duration-200 ${
                on ? "border-transparent text-foreground" : "border-border text-muted-foreground/60"}`}
              style={on ? { background: LINE_COLORS[i % LINE_COLORS.length] + "22" } : {}}
              data-testid={`series-toggle-${m.key}`}>
              <span className="h-2 w-2 rounded-full" style={{ background: on ? LINE_COLORS[i % LINE_COLORS.length] : "transparent", border: on ? "none" : "1px solid currentColor" }} />
              {metricLabel(m.key, m.label, lang)}
            </button>
          );
        })}
      </div>
      <div className="h-56 min-w-0 w-full">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={224}>
          <LineChart data={series} margin={{ top: 5, right: 10, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="ts" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} minTickGap={50}
              tickFormatter={(v) => (period === "24h" || period === "1h") ? new Date(v).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : new Date(v).toLocaleDateString("nl-NL", { day: "2-digit", month: "short" })} />
            <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} width={44} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              itemSorter={(item) => defs.findIndex((d) => d.key === item.dataKey)}
              labelFormatter={(v) => fmtDate(v)} />
            {defs.map((m, i) => (
              <Line key={m.key} type="monotone" dataKey={m.key} name={metricLabel(m.key, m.label, lang)} stroke={LINE_COLORS[i % LINE_COLORS.length]}
                strokeWidth={2} dot={false} isAnimationActive={false} hide={visible[m.key] === false} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {shown.length === 0 && <p className="mt-2 text-center text-xs text-muted-foreground">{lang === "en" ? "Select a sensor to display." : "Selecteer een sensor om weer te geven."}</p>}
    </div>
  );
}

export default function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { tick } = useRefresh();
  const { user } = useAuth();
  const [device, setDevice] = useState(null);
  const [period, setPeriod] = useState("24h");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [protocolOverview, setProtocolOverview] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [measureBusy, setMeasureBusy] = useState(false);

  useEffect(() => {
    api.get(`/devices/${id}`).then((r) => setDevice(r.data)).catch(() => navigate("/"));
  }, [id, navigate]);

  const fetchData = useCallback(async (silent) => {
    if (period === "custom" && (!range.start || !range.end)) return;
    setRefreshing(true);
    const params = period === "custom" ? { period, start: range.start, end: range.end } : { period };
    try {
      const [legacyResult, deviceResult, protocolResult, protocolSeriesResult] = await Promise.allSettled([
        api.get(`/devices/${id}/timeseries`, { params }),
        api.get(`/devices/${id}`),
        api.get(`/devices/${id}/v1/overview`),
        api.get(`/devices/${id}/v1/timeseries`, { params }),
      ]);
      if (deviceResult.status === "fulfilled") setDevice(deviceResult.value.data);
      if (protocolResult.status === "fulfilled") setProtocolOverview(protocolResult.value.data);
      const protocolData = protocolSeriesResult.status === "fulfilled" ? protocolSeriesResult.value.data : null;
      const legacyData = legacyResult.status === "fulfilled" ? legacyResult.value.data : null;
      setData(protocolData?.series?.length ? protocolData : (legacyData || protocolData || { metrics: [], series: [], current: {}, stats: {}, table: [] }));
    } catch {
      if (!silent) setData({ metrics: [], series: [], current: {}, stats: {}, table: [] });
    } finally {
      setRefreshing(false);
    }
  }, [id, period, range]);

  useEffect(() => { fetchData(false); }, [fetchData]);

  useEffect(() => { if (tick) fetchData(true); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const defs = completeMetricDefinitions(data?.metrics || device?.metrics || [], device?.sensors || {});
  const current = normalizeMetricValues({ ...(device?.last_metrics || {}), ...(data?.current || {}) });
  const series = normalizeMetricSeries(data?.series || []);

  const states = defs.filter((m) => m.kind === "state");
  const numeric = defs.filter((m) => m.kind !== "state" && m.key !== "status");
  const isoChartKeys = ["ISO_4um", "ISO_6um", "ISO_14um"];
  const isoDefs = isoChartKeys.map((k) => defs.find((m) => m.key === k)).filter(Boolean);
  const sensorChartKeys = ["low_saturation_pct_rh", "temperature_c", "conductivity_ns_m", "change_conductivity_pct", "relative_permittivity"];
  const sensorDefs = sensorChartKeys.map((k) => defs.find((m) => m.key === k)).filter(Boolean);
  const s = device ? (STATUS[device.status_badge_color || device.status_color] || STATUS.grey) : STATUS.grey;

  const exportCsv = () => {
    const rows = data?.table || [];
    if (!rows.length) { toast.error(t("Geen metingen om te exporteren", "No measurements to export")); return; }
    const cols = ["timestamp", ...defs.map((m) => m.key)];
    const lines = [cols.join(",")];
    rows.forEach((r) => lines.push([r.ts, ...defs.map((m) => r.metrics?.[m.key] ?? "")].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `metingen-${device.name}-${period}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(t("CSV geëxporteerd", "CSV exported"));
  };

  const measureNow = async () => {
    setMeasureBusy(true);
    try {
      await api.post(`/devices/${id}/commands`, { action: "MEASURE_NOW", parameters: {} });
      toast.success(t("Meetopdracht opgeslagen. De RevPi voert deze bij de eerstvolgende verbinding uit.", "Measurement command saved. The RevPi will execute it at the next connection."));
      await fetchData(true);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setMeasureBusy(false);
    }
  };

  if (!device) {
    return <div className="mx-auto max-w-7xl space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-80 w-full rounded-lg" /></div>;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground" onClick={() => navigate("/")} data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> {t("Terug naar dashboard", "Back to dashboard")}
      </Button>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-black tracking-tight">{device.name}</h1>
            <Badge variant="outline" className={`gap-1.5 border-0 ${s.bg} ${s.text}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} /> {device.status_label || statusLabel(device.status_badge_color || device.status_color, lang)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{device.description || t("Geen omschrijving", "No description")}</p>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {device.location || "—"}</span>
            <span className="flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" /> {device.serial_number || "—"}</span>
            <span className="flex items-center gap-1.5"><Cpu className="h-3.5 w-3.5" /> {defs.length} {t("meetwaarden", "measurements")}</span>
            <span className="flex items-center gap-1.5">
              {device.online ? <Wifi className="h-3.5 w-3.5 text-emerald-500" /> : <WifiOff className="h-3.5 w-3.5 text-slate-400" />}
              {device.online ? t("Online", "Online") : t("Geen recente verbinding", "No recent connection")} · {t("laatste meting", "last reading")} {fmtDate(data?.last_ts, lang)}
            </span>
          </div>
          <NextMeasurement device={{ ...device, next_expected: protocolOverview?.next_measurement_at || device.next_expected }} className="mt-3 rounded-lg border border-border bg-muted/15 px-3 py-2" />
        </div>
        <div className="flex w-full flex-col items-start gap-2 lg:w-auto lg:min-w-[590px] lg:items-end">
          <div className="flex flex-wrap gap-2">
            <ValueGuideButton />
            {isAdmin(user) && <Button variant="outline" onClick={() => navigate(`/devices/${id}/installation`)} className="gap-2" data-testid="installation-check-button"><ShieldCheck className="h-4 w-4" />{device.installation && !["completed", "skipped"].includes(device.installation.status) ? t("Installatie hervatten", "Resume installation") : t("Installatie controleren", "Check installation")}</Button>}
            {canMeasure(user) && canOperateDevice(user, device) && <Button onClick={measureNow} disabled={measureBusy} className="gap-2" data-testid="top-measure-now">
              {measureBusy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {measureBusy ? t("Meetopdracht opslaan…", "Saving measurement command…") : t("Nu meten", "Measure now")}
            </Button>}
          </div>
          <div className="flex w-full flex-nowrap justify-end gap-1.5 overflow-x-auto pb-1" data-testid="trend-period-select">
            {PERIODS.map((p) => (
              <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`period-${p.k}`}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                  period === p.k ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
                {t(p.nl, p.en)}
              </button>
            ))}
            <CustomRange active={period === "custom"} range={range} onApply={(r) => { setRange(r); setPeriod("custom"); }} />
            {refreshing && <RefreshCw className="ml-1 h-3.5 w-3.5 animate-spin self-center text-muted-foreground" />}
          </div>
        </div>
      </div>

      {isAdmin(user) && device.installation && !["completed", "skipped"].includes(device.installation.status) && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm" data-testid="installation-warning">
          <div><div className="font-semibold text-amber-700 dark:text-amber-300">{t("Installatie nog niet vrijgegeven", "Installation not yet released")}</div><div className="text-xs text-muted-foreground">{t(`De beheerder kan verdergaan bij stap ${device.installation.current_step} van 18.`, `The administrator can continue at step ${device.installation.current_step} of 18.`)}</div></div>
          <Button size="sm" onClick={() => navigate(`/devices/${id}/installation`)}>{t("Wizard hervatten", "Resume wizard")}</Button>
        </div>
      )}

      {data === null ? (
        <div className="space-y-4">
          <Skeleton className="h-20 rounded-lg" />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2"><Skeleton className="h-64 rounded-lg" /><Skeleton className="h-64 rounded-lg" /></div>
        </div>
      ) : (
        <>
          {/* Current values — compact unified panel */}
          <section className="space-y-2">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("Actuele waarden", "Current values")}</h2>
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8" data-testid="status-tiles">
              {states.map((m) => m.key === "warmup"
                ? <DerivedStatusCell key="system-status" current={current} t={t} />
                : <StateCell key={m.key} def={m} value={current[m.key]} />)}
              {numeric.map((m) => <MetricCell key={m.key} def={m} value={current[m.key]} />)}
            </div>
          </section>

          <CleanlinessProfile device={device} current={current} />

          {isAdmin(user) && (
            <section className="rounded-lg border border-border bg-card p-5" data-testid="device-data-usage">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="flex items-center gap-1 font-heading text-base font-bold tracking-tight">{t("Dataverbruik", "Data usage")}<InfoHint helpKey="data_usage" /></h2>
                  <p className="mt-1 text-xs text-muted-foreground">{device.data_usage ? `${t("Internetinterface", "Internet interface")}: ${device.data_usage.interface || "—"}` : t("De registratie start zodra RevPi 2.4.2 actief is.", "Tracking starts when RevPi 2.4.2 becomes active.")}</p>
                </div>
                {device.data_usage?.updated_at && <span className="text-xs text-muted-foreground">{t("Bijgewerkt", "Updated")}: {fmtDate(device.data_usage.updated_at, lang)}</span>}
              </div>
              <div className="mt-4 grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-3 lg:grid-cols-6">
                {[
                  [t("Ontvangen deze maand", "Received this month"), device.data_usage?.month_rx_bytes],
                  [t("Verzonden deze maand", "Sent this month"), device.data_usage?.month_tx_bytes],
                  [t("Totaal deze maand", "Total this month"), device.data_usage?.month_total_bytes],
                  [t("Ontvangen sinds start", "Received since start"), device.data_usage?.lifetime_rx_bytes],
                  [t("Verzonden sinds start", "Sent since start"), device.data_usage?.lifetime_tx_bytes],
                  [t("Totaal sinds start", "Total since start"), device.data_usage?.lifetime_total_bytes],
                ].map(([label, value]) => <div key={label} className="bg-card px-3 py-3"><div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-1 font-mono text-base font-bold">{fmtBytes(value, lang)}</div></div>)}
              </div>
              {device.data_usage?.tracking_started_at && <p className="mt-3 text-[11px] text-muted-foreground">{t("Registratie gestart", "Tracking started")}: {fmtDate(device.data_usage.tracking_started_at, lang)}. {t("Deze cijfers omvatten al het verkeer via de internetinterface, waaronder synchronisatie, Tailscale en systeemverkeer.", "These figures include all traffic through the internet interface, including synchronization, Tailscale and system traffic.")}</p>}
            </section>
          )}

          {/* Timelines */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <TimelineChart title={t("Oliereinheid (ISO 4406) — CS1000", "Oil cleanliness (ISO 4406) — CS1000")} defs={isoDefs} series={series} period={period} storageKey={`thrm_chart_${id}_iso`} />
            <TimelineChart title={t("Vochtverzadiging & slijtage — HLB1400", "Moisture saturation & wear — HLB1400")} defs={sensorDefs} series={series} period={period} storageKey={`thrm_chart_${id}_sensor`} />
          </div>

          {/* Table */}
          <div className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 className="font-heading text-base font-bold tracking-tight">{t("Recente metingen", "Recent measurements")}</h2>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Activity className="h-3.5 w-3.5" /> {data?.resolution === "raw" ? t("ruwe data", "raw data") : data?.resolution === "rollup_hour" ? t("per uur", "hourly") : t("per dag", "daily")} · {t("laatste", "latest")} {data?.table?.length || 0}
                </span>
                <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={exportCsv} disabled={!(data?.table || []).length} data-testid="export-measurements-csv">
                  <Download className="h-3.5 w-3.5" /> CSV
                </Button>
              </div>
            </div>
            <div className="max-h-[480px] overflow-auto">
              <Table data-testid="measurements-table">
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow>
                    <TableHead className="sticky left-0 bg-card">{t("Datum & tijd", "Date & time")}</TableHead>
                    {defs.map((m) => <TableHead key={m.key} className="whitespace-nowrap text-right">{metricLabel(m.key, m.label, lang)}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.table || []).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="sticky left-0 bg-card whitespace-nowrap font-mono text-xs text-muted-foreground">{fmtDate(row.ts)}</TableCell>
                      {defs.map((m) => <TableCell key={m.key} className="text-right font-mono text-muted-foreground">{num(row.metrics?.[m.key], lang)}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <FilterHistory deviceId={id} />

          {/* Monthly reports */}
          {!isDemo(user) && <DeviceReports deviceId={id} deviceName={device.name} />}
        </>
      )}

      {canManageFiltering(user) && canOperateDevice(user, device) && <section className="space-y-4 border-t border-border pt-6">
        <div>
          <h2 className="font-heading text-xl font-black tracking-tight">{t("Bediening en apparaatbeheer", "Controls and device management")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("Start een handmatige actie of pas de werking van de RevPi aan.", "Start a manual action or adjust how the RevPi operates.")}</p>
        </div>
        <DeviceControl deviceId={id} overview={protocolOverview} onRefresh={() => fetchData(true)} />
        {isAdmin(user) && <RemoteAccessPanel deviceId={id} />}
        {protocolOverview && (
          <details className="group rounded-lg border border-border bg-card" data-testid="technical-device-status">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-bold">
              <span>{t("Technische status en synchronisatie", "Technical status and synchronization")}</span>
              <span className="text-xs font-normal text-muted-foreground group-open:hidden">{t("Tonen", "Show")}</span>
              <span className="hidden text-xs font-normal text-muted-foreground group-open:inline">{t("Verbergen", "Hide")}</span>
            </summary>
            <div className="border-t border-border p-4"><ProtocolStatus overview={protocolOverview} /></div>
          </details>
        )}
      </section>}
    </div>
  );
}
